/**
 * YUMIX — Idle-time pre-fetcher.
 *
 * Call once the user is on the Dashboard to warm up heavy modules the user
 * is likely to need within the next ~30 seconds (camera, barcode scanner,
 * image pipelines). Uses requestIdleCallback so it NEVER contends with
 * first paint or above-the-fold interactions.
 *
 * Important: only WARMS the webpack chunk cache — does not mount any
 * React components. No memory pressure beyond the parsed JS.
 */
import { api } from '../context/AppContext';

let _warmed = false;

export function prewarmScanModules() {
  if (_warmed) return;
  _warmed = true;
  const run = () => {
    // Pre-parse the image compression + quality pipelines so the first
    // upload feels instantaneous.
    import(/* webpackPrefetch: true */ './imageCompression').catch(() => {});
    import(/* webpackPrefetch: true */ './imageQuality').catch(() => {});
    // Barcode & camera modules are isolated components — prewarm their chunks.
    import(/* webpackPrefetch: true */ '../components/BarcodeScanner').catch(() => {});
    import(/* webpackPrefetch: true */ '../components/SelfTimerCamera').catch(() => {});
    import(/* webpackPrefetch: true */ '../components/PhotoScanGuideModal').catch(() => {});
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1500);
  }
}

// ─────────────────────────────────────────────────────────────────────
// 🧠 Posture Vision Pre-warm (Iter. 405.e — parity with meal photo flow)
// Fires a fire-and-forget LLM ping the moment the user lands on the
// Evolution page so Gemini 2.5 Flash is already warm if they end up
// triggering the BlazePose → Vision fallback. Client-side throttle of
// 30 s per session prevents tab-switch spam; backend also throttles
// per worker so concurrent users coalesce.
// ─────────────────────────────────────────────────────────────────────
let _lastPosturePrewarmAt = 0;
const POSTURE_PREWARM_MIN_INTERVAL_MS = 30 * 1000;

export function prewarmPostureVision() {
  const now = Date.now();
  if (now - _lastPosturePrewarmAt < POSTURE_PREWARM_MIN_INTERVAL_MS) return;
  _lastPosturePrewarmAt = now;
  try {
    api.post('/evolution/posture-prewarm', {}, { timeout: 5000 }).catch(() => {});
  } catch {
    // absorb — perf hint, not a contract
  }
}
